import { createElement, useState, useRef, Fragment, ComponentProps, forwardRef } from 'react';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { DefaultColorPicker } from './DefaultColorPicker';
import { useColorPickerA11yInfo, useValidatedColorValues } from './hooks';
import { withLabels } from '../withLabels/withLabels';
import { DistributiveOmitStrict } from '../../utility-types';
import { postfixTestId } from '../../util/testId';
import { BaseButton } from '../Button/__internal__';
import { WithLabelsProps } from '../withLabels';
import { ColorObject, ColorPickerProps } from './types';
import { setRef } from '../../util/ref-util';
import { Popover } from '../Popover';
import bundle from './NovaComponents-ColorPicker-gui-icu.properties';
import cx from 'classnames';
import cn from './ColorPicker.css.json';
import './ColorPicker.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const ColorPicker = forwardRef<HTMLButtonElement, ColorPickerProps>((props, ref) => {
  const localize = useTextLocalizer();

  const { selectedColor, lastSelectedColor } = useValidatedColorValues(props.selectedColor);

  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const getColorPickerA11yInfo = useColorPickerA11yInfo();
  const { ariaLabel, tooltip } = getColorPickerA11yInfo({
    selectedColor,
    ariaLabel: props.ariaLabel,
    tooltip: props.tooltip,
    readOnly: props.readOnly,
  });

  const dt = useDesignTokens();

  const tokensNoColorChip = {
    bd: dt.c.colorUiBorder,
    'mark-bg': dt.c.colorAlert,
  };

  const colorswatchButtonTokens = {
    bd: dt.stateInputNova.normal.bd,
    bg: dt.stateInputNova.normal.bg,
    'hover-bd': dt.stateInputNova.hover.bd,
    'hover-bg': dt.stateInputNova.hover.bg,
    'active-bd': dt.stateInputNova.active.bd,
    'active-bg': dt.stateInputNova.active.bg,
    'disabled-bd': dt.stateInputNova.disabled.bd,
    'inner-bd': dt.c.colorUiBorder,
    'spacing-xs': dt.spacing.core.xs,
  };

  const style = {
    ...renderInlineCssVars(colorswatchButtonTokens, 'colorpicker-swatch'),
    ...renderInlineCssVars(tokensNoColorChip, 'colorpicker-no-color-swatch'),
  };

  const handleDismiss = () => {
    setPopoverIsOpen(false);
    setTimeout(() => buttonRef.current?.focus(), 0);
  };

  const handleColorSelection = (color: ColorObject) => {
    if ('onColorChange' in props) {
      props.onColorChange?.(color);
    }
    handleDismiss();
  };

  const MultiColorSwatchComponent = props.renderAutomaticSwatch
    ? props.renderAutomaticSwatch({
        getAttributes() {
          const multiColorAriaLabel = `${localize('selectColorAutomatic.label')} ${localize(
            'automatic.label'
          )} (${localize('multipleSmallCaseColors.label')})`;

          const multiColorTitle = `${localize('selectColorAutomatic.label')} ${localize(
            'automatic.label'
          )} (${localize('multipleSmallCaseColors.label')})`;

          return {
            ariaLabel: multiColorAriaLabel,
            title: multiColorTitle,
          };
        },
      })
    : null;

  const setButtonRef = (element: HTMLButtonElement | null) => {
    if (element) {
      buttonRef.current = element;
      setRef(ref, element);
    }
  };

  return (
    <Fragment>
      <BaseButton
        id={props.id}
        disabled={props.disabled}
        ref={setButtonRef}
        {...(props.readOnly && { role: 'img' })}
        {...(!(props.selectedColor.type === 'automatic' && props.renderAutomaticSwatch) && {
          tooltip,
        })}
        {...(!(props.selectedColor.type === 'automatic' && props.renderAutomaticSwatch) && {
          ariaLabel,
        })}
        ariaDescribedBy={props.ariaDescribedBy}
        onPress={() => {
          if (!props.readOnly) {
            setPopoverIsOpen(true);
          }
        }}
        className={cx(
          cn.dropdownColorSwatch,
          selectedColor.color === '' && cn.noColorSwatch,
          props.disabled && cn.disabled,
          props.readOnly && cn.readOnly,
          props.className
        )}
        style={{ ...style, ...props.style }}
        testId={postfixTestId(props.testId, 'colorSwatch')}
        tokens={() => ({
          filter: 'none',
        })}
      >
        {props.selectedColor.type === 'automatic' && props.renderAutomaticSwatch ? (
          MultiColorSwatchComponent
        ) : (
          <span
            className={cn.dropdownColorSwatchInner}
            style={{ background: selectedColor.color }}
            data-testid={postfixTestId(props.testId, 'color-swatch-inner')}
          />
        )}
      </BaseButton>
      <Popover
        role="dialog"
        className={cn.popover}
        isOpen={popoverIsOpen}
        isModal={activeIndex === 1}
        referenceElementRef={buttonRef}
        ariaLabel={localize('popper.aria')}
        onClose={() => setPopoverIsOpen(false)}
        focusLockProps={props.focusLockProps}
        testId={postfixTestId(props.testId, 'popover')}
      >
        <DefaultColorPicker
          {...props}
          testId={postfixTestId(props.testId, 'defaultColorPicker')}
          renderAutomaticSwatch={props.renderAutomaticSwatch}
          selectedColor={selectedColor}
          automaticColor={props.automaticColor}
          newColor={selectedColor}
          lastSelectedColor={lastSelectedColor}
          onColorSelect={handleColorSelection}
          selectedIndex={activeIndex}
          onTabItemSelect={setActiveIndex}
          onCancelPress={handleDismiss}
        />
      </Popover>
    </Fragment>
  );
});

const CPwithLabels = withLabels(ColorPicker);

type ColorPickerWithLabelsProps = DistributiveOmitStrict<
  ComponentProps<typeof CPwithLabels>,
  'suppressHtmlFor' | 'labelingAttr',
  WithLabelsProps
>;

export const ColorPickerWithLabels: any = forwardRef<HTMLButtonElement, ColorPickerWithLabelsProps>(
  (props, ref) => {
    const getColorPickerA11yInfo = useColorPickerA11yInfo();
    const { ariaLabel } = getColorPickerA11yInfo(props);
    return <CPwithLabels {...props} ref={ref} ariaLabel={ariaLabel} />;
  }
);
